import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Play, Radio, Tv, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
const toYouTubeEmbed = (url) => {
  // Accept youtu.be/xxx, youtube.com/watch?v=xxx, youtube.com/embed/xxx
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0&modestbranding=1` : null;
};
const toVimeoEmbed = (url) => {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? `https://player.vimeo.com/video/${m[1]}?autoplay=1&title=0&byline=0` : null;
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function TVWatch() {
  const { episodeId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    api.get(`/tv/episodes/${episodeId}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setError("Episode not found."))
      .finally(() => setLoading(false));
    // Scroll to top on episode change
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [episodeId]);

  if (loading) {
    return (
      <div className="min-h-[80vh] bg-ink flex items-center justify-center" data-testid="tv-watch-loading">
        <Loader2 size={24} className="animate-spin text-gold-400 mr-3" />
        <span className="text-white/60 uppercase tracking-[0.22em] text-sm">Cueing broadcast…</span>
      </div>
    );
  }

  if (error || !data?.episode) {
    return (
      <div className="min-h-[80vh] bg-ink flex flex-col items-center justify-center px-6 text-center" data-testid="tv-watch-not-found">
        <Tv size={38} className="text-gold-500/60 mb-5" />
        <p className="font-serif text-3xl text-white">Episode not found</p>
        <Link to="/tv" className="mt-8 inline-flex items-center gap-2 border border-gold-500/40 text-white/85 hover:text-gold-300 hover:border-gold-400 px-6 py-3 text-sm uppercase tracking-[0.16em] transition-colors">
          <ArrowLeft size={15} /> Back to HBTV
        </Link>
      </div>
    );
  }

  const { episode, show, up_next } = data;

  // Resolve the video source
  let embedSrc = null;
  let directSrc = null;
  const t = (episode.video_type || "placeholder").toLowerCase();
  if (episode.video_url && t !== "placeholder") {
    if (t === "youtube" || episode.video_url.includes("youtube") || episode.video_url.includes("youtu.be")) {
      embedSrc = toYouTubeEmbed(episode.video_url);
    } else if (t === "vimeo" || episode.video_url.includes("vimeo")) {
      embedSrc = toVimeoEmbed(episode.video_url);
    } else {
      directSrc = episode.video_url;
    }
  }

  const shareEpisode = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: episode.title, text: episode.description || "", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch { /* user cancelled share */ }
  };

  return (
    <div data-testid="tv-watch-page" className="bg-ink text-white">
      {/* Top bar */}
      <div className="border-b border-gold-500/15 bg-ink-royal/30">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <Link
            to={show ? `/tv/show/${show.slug}` : "/tv"}
            data-testid="tv-watch-back"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-gold-400 hover:text-gold-300 transition-colors"
          >
            <ArrowLeft size={13} /> {show ? `Back to ${show.title}` : "Back to HBTV"}
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.28em] text-white/40">Now watching on</span>
            <span className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-semibold">HBTV</span>
          </div>
        </div>
      </div>

      {/* Player + metadata */}
      <div className="mx-auto max-w-[1500px] px-4 md:px-10 pt-6 md:pt-10 pb-14 md:pb-20">
        <div className="grid gap-8 lg:grid-cols-12">
          {/* Left: Player + description */}
          <div className="lg:col-span-8">
            {/* Player */}
            <div className="relative aspect-video overflow-hidden border border-gold-500/25 bg-black shadow-[0_60px_120px_-40px_rgba(11,32,83,0.9)]" data-testid="tv-watch-player">
              {embedSrc ? (
                <iframe
                  src={embedSrc}
                  title={episode.title}
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                  className="w-full h-full"
                />
              ) : directSrc ? (
                <video
                  src={directSrc}
                  poster={episode.thumbnail}
                  controls
                  autoPlay
                  className="w-full h-full object-cover"
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                // Placeholder state — beautifully styled, invites admin action
                <div className="absolute inset-0 flex items-center justify-center" data-testid="tv-watch-placeholder">
                  {episode.thumbnail && (
                    <img src={episode.thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/40" />
                  <div className="relative text-center px-8 max-w-lg">
                    <motion.div
                      animate={{ scale: [1, 1.05, 1], opacity: [0.9, 1, 0.9] }}
                      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                      className="mx-auto w-20 h-20 rounded-full bg-gold-500/15 border border-gold-500/40 flex items-center justify-center mb-6"
                    >
                      <Radio size={30} className="text-gold-400" strokeWidth={1.4} />
                    </motion.div>
                    <p className="text-[11px] uppercase tracking-[0.3em] text-gold-400 mb-3">Broadcast Preview</p>
                    <p className="font-serif text-white text-2xl md:text-3xl leading-tight">Coming soon to HBTV</p>
                    <p className="mt-3 text-white/60 text-sm leading-relaxed">
                      This episode is queued for broadcast. Subscribe to the daily devotional and we'll notify you the moment it goes live.
                    </p>
                    <Link
                      to="/#home-newsletter"
                      className="mt-6 inline-flex items-center gap-2 border border-gold-500/40 hover:border-gold-400 text-white/85 hover:text-gold-300 px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] transition-colors"
                    >
                      Get notified
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Metadata */}
            <div className="mt-6 md:mt-8">
              <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400" data-testid="tv-watch-showname">
                {show?.title} · Episode {episode.episode_number}
              </p>
              <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
                <h1 className="font-serif text-3xl md:text-5xl leading-[1.05] text-white" data-testid="tv-watch-title">
                  {episode.title}
                </h1>
                <button
                  onClick={shareEpisode}
                  data-testid="tv-watch-share"
                  className="inline-flex items-center gap-2 border border-gold-500/40 hover:border-gold-400 text-white/85 hover:text-gold-300 px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] transition-colors"
                >
                  <Share2 size={13} /> Share
                </button>
              </div>
              {episode.duration && (
                <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-white/40">
                  Runtime · {episode.duration}
                </p>
              )}
              {episode.description && (
                <p className="mt-6 text-white/75 leading-relaxed max-w-3xl">{episode.description}</p>
              )}
            </div>
          </div>

          {/* Right: Up Next */}
          <aside className="lg:col-span-4">
            <p className="text-[11px] uppercase tracking-[0.28em] text-gold-500 mb-5">Up next on {show?.title || "HBTV"}</p>
            {up_next && up_next.length > 0 ? (
              <ul className="space-y-3" data-testid="tv-watch-upnext">
                {up_next.map((e) => (
                  <li key={e.id}>
                    <Link
                      to={`/tv/watch/${e.id}`}
                      data-testid={`tv-upnext-${e.id}`}
                      className="group flex gap-3 border border-gold-500/15 hover:border-gold-400 bg-ink-paper/40 hover:bg-ink-paper transition-colors duration-200 p-2.5"
                    >
                      <div className="relative w-32 aspect-video shrink-0 overflow-hidden">
                        <img src={e.thumbnail} alt={e.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" />
                        <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-ink/40 transition-opacity">
                          <span className="w-8 h-8 rounded-full bg-gold-500 text-ink flex items-center justify-center">
                            <Play size={13} className="ml-0.5 fill-current" strokeWidth={0} />
                          </span>
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm leading-snug line-clamp-2 group-hover:text-gold-200 transition-colors">{e.title}</p>
                        {e.duration && (
                          <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-white/40">{e.duration}</p>
                        )}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-white/50 text-sm">No more episodes queued right now.</p>
            )}

            {show && (
              <Link
                to={`/tv/show/${show.slug}`}
                data-testid="tv-watch-showall"
                className="mt-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-gold-400 hover:text-gold-300 transition-colors"
              >
                See all {show.title} episodes →
              </Link>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
